import React, {
  useMemo,
  useRef,
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
} from "react";
import { MarketButton } from "components-sbk/src/components/MarketButton/MarketButton";
import type { MarketButtonType } from "../../../../MarketButton/types";
import { Competitor } from "components-sbk/src/components/Competitor/Competitor";
import { TeamColor } from "components-sbk/src/components/TeamColor/TeamColor";
import { Icon } from "components/src/components/Icon/Icon";
import { Carousel } from "components/src/components/Carousel/Carousel";

import type {
  MarketTemplateSquaresCornerType,
  MarketTemplateMarketsType,
  MarketTemplateOnSelectionClickType,
  MarketTemplateProps,
  MarketTemplateUpdateMarketType,
} from "components-sbk/src/components/MarketTemplate/types";
import cx from "components/src/utils/cx";
import {
  requestTimeout,
  clearRequestTimeout,
} from "shared/src/utils/requestTimeout";
import { MappedSelection } from "../../Market/components/MappedSelection";
import type { MarketHeaderType } from "../../MarketHeader/types";
import { useIdRef } from "shared/src/hooks/useIdRef";
import { ThemeModeType } from "shared/src/types";
import { createMetaData } from "../../Market/js/createMetaData";

// Private configuration
const CONFIG = {
  offsets: {
    top: 24,
    left: 24,
  },
  minGridWidth: 88,
  minGridHeight: 50,
  sharedSize: 44, // used for topContainer height and side container width
};

const _defaultFn = () => {};

export const ScrollableLayout = ({
  markets,
  sport,
  competitionType,
  marketHeader,
  corner = {
    icon: null,
    bgColor: `neutrals-black`,
  },
  onSelectionClick,
  priceDisplayFormatter,
  isSelectedBuilder,
  updateMarket = _defaultFn,
  mode,
  ...rest
}: {
  markets: MarketTemplateMarketsType;
  marketHeader?: MarketHeaderType;
  corner?: MarketTemplateSquaresCornerType;
  sport?: string;
  competitionType?: string;
  onSelectionClick?: MarketTemplateOnSelectionClickType;
  priceDisplayFormatter?: MarketTemplateProps["priceDisplayFormatter"];
  isSelectedBuilder?: MarketTemplateProps["isSelectedBuilder"];
  updateMarket?: MarketTemplateUpdateMarketType;
  mode?: ThemeModeType;
}) => {
  // const [isScrolling, setIsScrolling] = useState(false);
  // const [marketsSettled, setMarketsSettled] = useState(true);

  const id = useIdRef({ str: "cui__market-template-scrollable--" });

  const isValidMarkets = Array.isArray(markets) && markets.length;
  const isValidMarketHeader =
    Array.isArray(marketHeader?.labels) && !!marketHeader.labels;

  const flatSelections = useMemo(
    () => isValidMarkets && markets.map((market) => market.selections).flat(),
    [markets]
  );

  const { scrollingMarketsInitialAnchor } = rest as MarketTemplateProps;
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, id: string) => {
      if (onSelectionClick) {
        const selection =
          Array.isArray(flatSelections) &&
          flatSelections.find((selection) => selection.id === id);
        selection && onSelectionClick({ e, id, updateMarket, selection });
      }
    },
    [markets, onSelectionClick, isSelectedBuilder]
  );

  const competitors = useMemo(
    () =>
      isValidMarkets &&
      markets?.map((market, i) => ({
        ...market.competitor,
        "data-filter-group": market.competitor?.team,
        "data-filter-group-row": i,
      })),
    [markets]
  );

  const selections = useMemo(
    () =>
      isValidMarkets &&
      markets
        ?.map((market) => {
          return {
            selections: market.selections.map((selection) => {
              return {
                ...selection,
                dataFilterGroup: market.competitor?.team,
              };
            }),
          };
        })
        .map((market) => market.selections),
    [markets]
  );

  const groupedSelections = useMemo(
    () =>
      Array.isArray(selections) &&
      selections.reduce((acc, selections, index) => {
        selections.forEach((selection, i) => {
          if (!(i in acc)) {
            acc[i] = [];
          }
          selection["dataFilterGroupRow"] = index;
          acc[i].push(selection);
        });
        return acc;
      }, {}),
    [selections]
  );

  const selectionColumns = useMemo(
    () =>
      Object.entries(groupedSelections).map(([index, selections]) => {
        // const _labels = typeof marketHeader.labels === "object" ? marketHeader?.labels.map(label => )
        return (
          <div
            className="cui__market-template-scrollable--column cui-w-full cui-max-w-[60px] md:cui-max-w-[88px] lg:cui-max-w-[120px]"
            // style={{ maxWidth: CONFIG.minGridWidth }}
            key={`${id}-selection-colum-${index}`}
          >
            <div className="cui-flex cui-justify-center heading-xs cui-text-fg-moderate">
              <span className="cui-truncate">
              {isValidMarketHeader &&
              marketHeader.labels &&
              typeof marketHeader.labels[0] === "string"
                ? marketHeader.labels[index]
                : null}
              {isValidMarketHeader &&
              marketHeader.labels &&
              typeof marketHeader.labels[0] === "object"
                ? marketHeader.labels[index]?.text
                : null}
                </span>
            </div>
            {selections.map((selection) => {
              const {
                dataFilterGroup = "",
                dataFilterGroupRow = "",
                ..._selection
              } = selection;

              const metadata = createMetaData({
                competitor: competitors[dataFilterGroupRow],
                marketHeader,
                sport,
                i: dataFilterGroupRow,
              } as any);

              return (
                <div
                  className="cui-mt-1 cui-w-full cui-min-w-[60px] md:cui-min-w-[88px] lg:cui-min-w-[120px]"
                  style={{
                    display: "block",
                    height: CONFIG.minGridHeight,
                  }}
                  key={_selection.id}
                  data-filter-group={dataFilterGroup}
                  data-filter-group-row={dataFilterGroupRow}
                >
                  <MarketButton
                    {..._selection}
                    fluid={true}
                    fullHeight={true}
                    metadata={metadata}
                    onClick={handleClick}
                    isSelectedBuilder={isSelectedBuilder}
                    priceDisplayFormatter={priceDisplayFormatter}
                  />
                </div>
              );
            })}
          </div>
        );
      }),
    [groupedSelections, isSelectedBuilder]
  );

  // Defaults to center anchor, leading is from the beginning.
  const centeredAnchor = Math.floor(selectionColumns.length / 2);
  const _initialIndex =
    scrollingMarketsInitialAnchor === "center"
      ? centeredAnchor
      : scrollingMarketsInitialAnchor === "leading"
      ? 0
      : typeof scrollingMarketsInitialAnchor === "number"
      ? scrollingMarketsInitialAnchor
      : centeredAnchor;

  return (
    <div className="cui-flex cui-w-full">
      <div className="cui-w-[200px] cui-mt-1">
        <div className="cui-h-[18px]" />
        {Array.isArray(competitors) &&
          competitors.map((competitor, i) => {
            return (
              <Competitor
                {...competitor}
                className="cui-my-2px"
                style={{ minHeight: CONFIG.minGridHeight }}
                mode={mode}
                sport={sport}
                competitionType={competitionType}
                tabbable={false}
                key={competitor?.id ?? `${id}--competitor-${i}`}
              />
            );
          })}
      </div>
      <div className="cui-w-[calc(100%-200px)] cui-relative">
        <div className="cui-absolute cui-inset-0">
          <Carousel
            controlSize="sm"
            showControls="always"
            showPagination={false}
            itemSpacing={0}
            showFadeRight={false}
            fadeType="dark"
            fadeTopOffset={20}
            initialIndex={_initialIndex}
            controlButtonsHideAtEdge={true}
            centerSlides={true}
            disableMouseWheel={true}
            innerJustify="end"
            skipLink={{ label: "Skip the Market", anchorTag: "" }}
          >
            {selectionColumns}
          </Carousel>
        </div>
      </div>
    </div>
  );
};
